import React, { useState, useEffect } from "react";
import {
  Grid,
  Divider,
  RadioGroup,
  FormControlLabel,
  Typography,
  Select,
  MenuItem,
  Box,
  Switch,
  FormControl,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Footer from "examples/Footer";
import {
  ConfigCard,
  BlueButton,
  StyledRadio,
} from "styles";
import BannerPreview from "components/BannerPreview";
import API_BASE_URL from "config/api";
import LivePreview from "components/LivePreview";

const BannerLayout = () => {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [bannerSettings, setBannerSettings] = useState(null);
  const [userMeData, setUserMeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewTabValue, setPreviewTabValue] = useState(0);
  const [activeLang, setActiveLang] = useState("tr");
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functionality: false,
    analytics: false,
    marketing: false,
  });
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentDomainSettings, setCurrentDomainSettings] = useState(null);
  const [currentLanguageSettings, setCurrentLanguageSettings] = useState(null);
  const [cookies, setCookies] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    const loadUserData = async () => {
      const cachedUserData = sessionStorage.getItem("userMeData");
      if (cachedUserData) {
        setUserMeData(JSON.parse(cachedUserData));
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/me`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
        });
        const data = await response.json();
        sessionStorage.setItem("userMeData", JSON.stringify(data));
        setUserMeData(data);
      } catch (error) {
        console.error("Error loading user data:", error);
        showSnackbar("Kullanıcı verileri yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  useEffect(() => {
    const loadBannerSettings = async () => {
      if (!userMeData) return;

      try {
        setLoading(true);
        const cachedSettings = sessionStorage.getItem("bannerSettings");

        if (cachedSettings) {
          const parsedSettings = JSON.parse(cachedSettings);
          setBannerSettings(parsedSettings);
          setDomains(Object.keys(parsedSettings));

          if (Object.keys(parsedSettings).length > 0) {
            const firstDomain = Object.keys(parsedSettings)[0];
            setSelectedDomain(firstDomain);
            setCurrentDomainSettings(parsedSettings[firstDomain]);
            if (parsedSettings[firstDomain].bannerSettings[activeLang]) {
              setCurrentLanguageSettings(
                parsedSettings[firstDomain].bannerSettings[activeLang]
              );
            } else {
              const availableLangs = Object.keys(
                parsedSettings[firstDomain].bannerSettings
              );
              if (availableLangs.length > 0) {
                setActiveLang(availableLangs[0]);
                setCurrentLanguageSettings(
                  parsedSettings[firstDomain].bannerSettings[availableLangs[0]]
                );
              }
            }
          }
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/banner-settings/by-tenant?tenantId=${userMeData.user.clients[0].clientId}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
          }
        );
        const data = await response.json();
        sessionStorage.setItem("bannerSettings", JSON.stringify(data));
        setBannerSettings(data);
        setDomains(Object.keys(data));

        if (Object.keys(data).length > 0) {
          const firstDomain = Object.keys(data)[0];
          setSelectedDomain(firstDomain);
          setCurrentDomainSettings(data[firstDomain]);
          if (data[firstDomain].bannerSettings[activeLang]) {
            setCurrentLanguageSettings(
              data[firstDomain].bannerSettings[activeLang]
            );
          } else {
            const availableLangs = Object.keys(
              data[firstDomain].bannerSettings
            );
            if (availableLangs.length > 0) {
              setActiveLang(availableLangs[0]);
              setCurrentLanguageSettings(
                data[firstDomain].bannerSettings[availableLangs[0]]
              );
            }
          }
        }
      } catch (error) {
        console.error("Error loading banner settings:", error);
        showSnackbar("Banner ayarları yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadBannerSettings();
  }, [userMeData]);

  useEffect(() => {
    const loadCookieData = async () => {
      if (!userMeData) return;

      try {
        setLoading(true);
        const cachedSettings = sessionStorage.getItem("userCookies");

        if (cachedSettings && Object.keys(cachedSettings).length > 0) {
          const parsedSettings = JSON.parse(cachedSettings);
          setCookies(parsedSettings);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/cookie-definition/all?tenantId=${userMeData.user.clients[0].clientId}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            },
          }
        );
        const data = await response.json();

        if (Object.keys(data).length > 0) {
          sessionStorage.setItem("userCookies", JSON.stringify(data));
          setCookies(data);
        }
      } catch (error) {
        console.error("Error loading cookies:", error);
        showSnackbar("Çerezler yüklenirken hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    loadCookieData();
  }, [userMeData]);

  useEffect(() => {
    if (selectedDomain && bannerSettings && bannerSettings[selectedDomain]) {
      const domainSettings = bannerSettings[selectedDomain];
      setCurrentDomainSettings(domainSettings);

      const defaultLang = domainSettings.defaultLanguage || activeLang;

      if (domainSettings.bannerSettings[defaultLang]) {
        setActiveLang(defaultLang);
        setCurrentLanguageSettings(domainSettings.bannerSettings[defaultLang]);
      } else if (domainSettings.bannerSettings[activeLang]) {
        setCurrentLanguageSettings(domainSettings.bannerSettings[activeLang]);
      } else {
        const availableLangs = Object.keys(domainSettings.bannerSettings);
        if (availableLangs.length > 0) {
          const fallbackLang = availableLangs[0];
          setActiveLang(fallbackLang);
          setCurrentLanguageSettings(
            domainSettings.bannerSettings[fallbackLang]
          );
        }
      }
    }
  }, [selectedDomain]);

  useEffect(() => {
    if (
      currentDomainSettings &&
      currentDomainSettings.bannerSettings[activeLang]
    ) {
      setCurrentLanguageSettings(
        currentDomainSettings.bannerSettings[activeLang]
      );
    }
  }, [activeLang, currentDomainSettings]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCategoryToggle = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDomainChange = (event) => {
    setSelectedDomain(event.target.value);
  };

  const handlePreviewTabChange = (event, newValue) => {
    setPreviewTabValue(newValue);
  };

  const handleBannerTypeChange = (event) => {
    const newSettings = { ...bannerSettings };
    newSettings[selectedDomain].themeSettings.type = event.target.value;
    setBannerSettings(newSettings);
  };

  const handleBannerPositionChange = (event) => {
    const newSettings = { ...bannerSettings };
    newSettings[selectedDomain].themeSettings.position = event.target.value;
    setBannerSettings(newSettings);
  };

  const handlePreferencesPositionChange = (event) => {
    const newSettings = { ...bannerSettings };
    newSettings[selectedDomain].themeSettings.preferencesPosition =
      event.target.value;
    setBannerSettings(newSettings);
  };

  const handleShowDetailsChange = (event) => {
    const newSettings = { ...bannerSettings };
    if (!newSettings[selectedDomain]) return;
    newSettings[selectedDomain].themeSettings.showCookieDetails =
      event.target.value === "on";
    setBannerSettings(newSettings);
  };

  const handlePreferenceChange = (category) => (event) => {
    setCookiePreferences({
      ...cookiePreferences,
      [category]: event.target.checked,
    });
  };

  const handleLangChange = (e) => {
    setActiveLang(e.target.value);
  };

  const handleAcceptAll = () => {
    setCookiePreferences({
      necessary: true,
      functionality: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    setCookiePreferences({
      necessary: true,
      functionality: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleCustomize = () => {
    setOpenModal(!openModal);
  };

  const handleAcceptSelection = () => {
    setOpenModal(false);
  };

  const handleSave = async () => {
    if (!bannerSettings || !selectedDomain || !userMeData) return;

    try {
      setSaving(true);
      const currentSettings = bannerSettings[selectedDomain];
      const { themeSettings, id } = currentSettings;

      const payload = {
        siteId: id,
        type: themeSettings.type,
        position: themeSettings.position,
        preferencesPosition: themeSettings.preferencesPosition,
        showCookieDetails: themeSettings.showCookieDetails !== false,
      };

      const response = await fetch(
        `${API_BASE_URL}/theme-settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Güncelleme başarısız oldu");
      }

      const updatedSettings = { ...bannerSettings };
      updatedSettings[selectedDomain].themeSettings = {
        ...updatedSettings[selectedDomain].themeSettings,
        ...payload,
      };

      sessionStorage.setItem("bannerSettings", JSON.stringify(updatedSettings));
      setBannerSettings(updatedSettings);

      showSnackbar("Ayarlar başarıyla kaydedildi", "success");
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      showSnackbar("Ayarlar kaydedilirken hata oluştu", "error");
    } finally {
      setSaving(false);
    }
  };

  if (
    selectedDomain &&
    bannerSettings &&
    bannerSettings[selectedDomain] &&
    (!bannerSettings[selectedDomain].bannerSettings ||
      Object.keys(bannerSettings[selectedDomain].bannerSettings).length === 0)
  ) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
          flexDirection="column"
        >
          <Typography variant="h6" mb={2}>
            Bir sitenizde dil ayarlarını tamamlamadınız.
          </Typography>
          <BlueButton onClick={() => (window.location.href = "/configuration/language")}>
            Dil Ayarlarına Git
          </BlueButton>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (
    !bannerSettings ||
    !selectedDomain ||
    !currentDomainSettings ||
    !currentLanguageSettings
  ) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <CircularProgress size={50} />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const themeSettings = currentDomainSettings.themeSettings;
  const availableLangs = Object.keys(currentDomainSettings.bannerSettings);

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <ConfigCard>
              <MDBox p={3}>
                <MDTypography
                  variant="h5"
                  fontWeight="medium"
                  mb={3}
                  color="info"
                >
                  Banner Düzeni
                </MDTypography>

                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  Domain
                </MDTypography>
                <FormControl
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.375rem",
                      background: "#fafbfc",
                      fontSize: 16,
                      padding: "4px 12px",
                      "& .MuiSelect-icon": {
                        color: "#000",
                        right: "12px",
                      },
                    },
                  }}
                >
                  <Select
                    value={selectedDomain}
                    onChange={handleDomainChange}
                    IconComponent={(props) => (
                      <KeyboardArrowDownIcon {...props} fontSize="medium" />
                    )}
                    sx={{
                      minHeight: 48,
                      fontSize: 16,
                      borderRadius: "12px",
                      ".MuiSelect-select": {
                        padding: "14px 32px 14px 12px",
                      },
                      "& .MuiSelect-icon": {
                        display: "block !important",
                      },
                    }}
                  >
                    {domains.map((domain) => (
                      <MenuItem key={domain} value={domain}>
                        {domain}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <MDTypography variant="body2" fontWeight="medium" mb={1}>
                  Banner Tipi
                </MDTypography>
                <RadioGroup
                  value={themeSettings.type}
                  onChange={handleBannerTypeChange}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="banner"
                        control={<StyledRadio />}
                        label="Bar"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="dialog"
                        control={<StyledRadio />}
                        label="Dialog"
                      />
                    </Grid>
                  </Grid>
                </RadioGroup>

                {themeSettings.type === "banner" && (
                  <>
                    <Divider sx={{ my: 2 }} />

                    <MDTypography variant="body2" fontWeight="medium" mb={1}>
                      Banner Pozisyonu (İlk Ekran)
                    </MDTypography>
                    <RadioGroup
                      value={themeSettings.position}
                      onChange={handleBannerPositionChange}
                    >
                      <FormControlLabel
                        value="bottom-left"
                        control={<StyledRadio />}
                        label="Alt Sol"
                      />
                      <FormControlLabel
                        value="bottom-right"
                        control={<StyledRadio />}
                        label="Alt Sağ"
                      />
                      <FormControlLabel
                        value="left"
                        control={<StyledRadio />}
                        label="Kompakt Sol"
                      />
                      <FormControlLabel
                        value="right"
                        control={<StyledRadio />}
                        label="Kompakt Sağ"
                      />
                    </RadioGroup>

                    <Divider sx={{ my: 2 }} />

                    <MDTypography variant="body2" fontWeight="medium" mb={1}>
                      Özelleştirme Menüsü Pozisyonu
                    </MDTypography>
                    <RadioGroup
                      value={themeSettings.preferencesPosition}
                      onChange={handlePreferencesPositionChange}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControlLabel
                            value="left"
                            control={<StyledRadio />}
                            label="Sol"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            value="right"
                            control={<StyledRadio />}
                            label="Sağ"
                          />
                        </Grid>
                      </Grid>
                    </RadioGroup>

                    <Divider sx={{ my: 2 }} />

                    <MDTypography variant="body2" fontWeight="medium" mb={1}>
                      Çerez Detaylarını Göster
                    </MDTypography>
                    <MDBox>
                      <Switch
                        checked={themeSettings.showCookieDetails !== false}
                        onChange={(e) =>
                          handleShowDetailsChange({
                            target: { value: e.target.checked ? "on" : "off" },
                          })
                        }
                        color="primary"
                      />
                      <MDTypography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", mt: 1 }}
                      >
                        Çerez bulunmayan kategorileriniz için izin almak adına
                        Çerez Detaylarını Göster seçeneğini kapatınız.
                      </MDTypography>
                    </MDBox>
                  </>
                )}
                {themeSettings.type === "dialog" && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <MDTypography variant="body2" fontWeight="medium" mb={1}>
                      Özelleştirme Menüsü Pozisyonu
                    </MDTypography>
                    <MDTypography variant="caption" color="textSecondary">
                      Dialog tipi banner için özelleştirme menüsü merkezde
                      gösterilir
                    </MDTypography>

                    <Divider sx={{ my: 2 }} />
                    <MDTypography variant="body2" fontWeight="medium" mb={1}>
                      Çerez Detaylarını Göster
                    </MDTypography>
                    <Switch
                      checked={themeSettings.showCookieDetails !== false}
                      onChange={(e) =>
                        handleShowDetailsChange({
                          target: { value: e.target.checked ? "on" : "off" },
                        })
                      }
                      color="primary"
                    />
                  </>
                )}

                <MDBox mt={2}>
                  <BlueButton
                    fullWidth
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={
                      saving ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </BlueButton>
                </MDBox>
              </MDBox>
            </ConfigCard>
          </Grid>

          <Grid item xs={12} md={8}>
            <LivePreview 
              cookieContentState={{
                banner: <BannerPreview
                  themeSettings={themeSettings}
                  currentLanguageSettings={currentLanguageSettings}
                  currentDomainSettings={currentDomainSettings}
                  cookies={cookies}
                  activeLang={activeLang}
                  previewTabValue={previewTabValue}
                  openModal={openModal}
                  cookiePreferences={cookiePreferences}
                  expandedCategories={expandedCategories}
                  availableLangs={availableLangs}
                  onLangChange={handleLangChange}
                  onPreviewTabChange={handlePreviewTabChange}
                  onCustomize={handleCustomize}
                  onAcceptAll={handleAcceptAll}
                  onRejectAll={handleRejectAll}
                  onAcceptSelection={handleAcceptSelection}
                  onPreferenceChange={handlePreferenceChange}
                  onCategoryToggle={handleCategoryToggle}
                  onSnackbar={showSnackbar}
                />
              }}
              selectedDomain={selectedDomain}
              defaultLanguage={currentDomainSettings.defaultLanguage}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default BannerLayout;
